import React, { useCallback, useEffect, useMemo, useState,useContext } from 'react';
import { api } from '../config/api';
import { Plus, ArrowLeft, Eye, ClipboardList, Trash2, Pencil, X, Search } from 'lucide-react';
import toast from '../utils/toast';
import { useLocation } from 'react-router-dom';
import { formatDisplayDate, normalizeDateInput, toDateInputValue, toDateObject } from '../utils/dateFormat';
import SearchBar from '../components/SearchBar';
import { AuthContext } from '../context/AuthContext';
import {
  masterAccentButton,
  masterHeaderCard,
  masterHeaderIcon,
  masterInput,
  masterInputLabel,
  masterPageShell,
  masterPanelPadding,
  masterPrimaryButton,
  masterReadOnly,
  masterSearchInput,
  masterSearchWrap,
  masterSelect,
  masterTitle,
  masterTableData,
  masterToolbar,
  masterTableID
} from '../components/masterPageStyles';


const emptyForm = {
  po_id: '',
  po_number: '',
  supplier_id: '',
  po_date: toDateInputValue(new Date()),
  expected_delivery_date: '',
 created_by: '',
  remarks: '',
  status: 'Draft',
  items: [{ medicine_id: '', medicine_name: '', company_name: '', ordered_quantity: 1, purchase_price: 0, mrp: 0, total_amount: 0 }]
};


const PurchaseOrder = () => {
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const [view, setView] = useState('form');
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [grns, setGrns] = useState([]);
  const [editId, setEditId] = useState(null);
  const [selectedPO, setSelectedPO] = useState(null);
 const [formData, setFormData] = useState(() => {
    return {
      ...emptyForm,
      created_by: user?.fullName || '' 
    };
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    if (user?.fullName && !editId) {
      setFormData(prev => ({ ...prev, created_by: user.fullName }));
    }
  }, [user, editId]);

  useEffect(() => {
    fetchPOs();
    fetchSuppliers();
    fetchMedicines();
    fetchStocks();
    fetchGRNs();
  }, []);

  useEffect(() => {
    const prefillItems = location.state?.prefillItems;
    if (!Array.isArray(prefillItems) || !prefillItems.length) return;

    setEditId(null);
    setSelectedPO(null);
    setFormData((prev) => ({
      ...emptyForm,
      supplier_id: prev.supplier_id || '',
      created_by: prev.created_by || '',
      remarks: prev.remarks || '',
      items: prefillItems.map((item) => {
        const qty = Number(item?.ordered_quantity || 0) || 1;
        const price = Number(item?.purchase_price || 0) || 0;
        return {
          medicine_id: item?.medicine_id || '',
          medicine_name: item?.medicine_name || '',
          company_name: item?.company_name || '',
          ordered_quantity: qty,
          purchase_price: price,
          mrp: Number(item?.mrp || 0) || 0,
          total_amount: qty * price
        };
      })
    }));
    setView('form');
    window.history.replaceState({}, document.title);
  }, [location.state]);

  const fetchPOs = async () => {
    try {
      const res = await api.get('/purchase-orders');
      setPurchaseOrders(res.data || []);
    } catch (err) {
      console.error('Error fetching POs', err);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const res = await api.get('/master/suppliers/all');
      if (res.data?.success) {
        setSuppliers(res.data.data || []);
      } else {
        setSuppliers(res.data || []);
      }
    } catch (err) {
      console.error('Error fetching suppliers', err);
    }
  };

  const fetchMedicines = async () => {
    try {
      const res = await api.get('/master/medicine/all');
      if (res.data?.success) {
        setMedicines(res.data.data || []);
      } else {
        setMedicines(res.data || []);
      }
    } catch (err) {
      console.error('Error fetching medicines', err);
    }
  };

  const fetchStocks = async () => {
    try {
      const res = await api.get('/stock');
      setStocks(res.data || []);
    } catch (err) {
      console.error('Error fetching stocks', err);
    }
  };

  const fetchGRNs = async () => {
    try {
      const res = await api.get('/grn');
      setGrns(res.data || []);
    } catch (err) {
      console.error('Error fetching GRNs', err);
    }
  };

  const supplierMap = useMemo(() => {
    const map = new Map();
    suppliers.forEach((s) => {
      if (s?._id) map.set(s._id, s);
      if (s?.supplier_id) map.set(s.supplier_id, s);
    });
    return map;
  }, [suppliers]);

  const getSupplierLabel = useCallback((poSupplier) => {
    if (!poSupplier) return 'N/A';
    if (typeof poSupplier === 'object') {
      return poSupplier.supplier_name || poSupplier.name || 'N/A';
    }
    const found = supplierMap.get(poSupplier);
    return found ? `${found.supplier_name}` : poSupplier;
  }, [supplierMap]);

  const medicineMap = useMemo(() => {
    const map = new Map();
    medicines.forEach((m) => {
      if (m?._id) map.set(m._id, m);
      if (m?.medicine_id) map.set(m.medicine_id, m);
      if (m?.medicine_code) map.set(m.medicine_code, m);
    });
    return map;
  }, [medicines]);

  const latestStockByMedicine = useMemo(() => {
    const map = new Map();
    stocks.forEach((stock) => {
      const medicineKey = String(stock?.medicine_id || '').trim();
      if (!medicineKey) return;
      const current = map.get(medicineKey);
      const stockTime = new Date(stock?.last_updated || 0).getTime();
      const currentTime = new Date(current?.last_updated || 0).getTime();
      if (!current || stockTime >= currentTime) {
        map.set(medicineKey, stock);
      }
    });
    return map;
  }, [stocks]);

  const latestGrnItemByMedicine = useMemo(() => {
    const map = new Map();

    const getTimestamp = (grn) => {
      const receivedTime = new Date(grn?.received_date || 0).getTime();
      if (!Number.isNaN(receivedTime) && receivedTime > 0) return receivedTime;

      const createdTime = new Date(grn?.createdAt || 0).getTime();
      return Number.isNaN(createdTime) ? 0 : createdTime;
    };

    grns.forEach((grn) => {
      const grnTime = getTimestamp(grn);
      (grn?.items || []).forEach((item) => {
        const medicineKey = String(item?.medicine_id || '').trim();
        if (!medicineKey) return;

        const current = map.get(medicineKey);
        const currentTime = current?.timestamp || 0;

        if (!current || grnTime >= currentTime) {
          map.set(medicineKey, {
            timestamp: grnTime,
            purchase_price: Number(item?.purchase_price ?? 0) || 0,
            mrp: Number(item?.mrp ?? 0) || 0
          });
        }
      });
    });

    return map;
  }, [grns]);

  const supplierMedicines = useMemo(() => {
    if (!formData.supplier_id) return [];
    const selectedId = String(formData.supplier_id).trim();
    const selectedMedicineIds = new Set(
      (formData.items || []).map((item) => String(item?.medicine_id || '').trim()).filter(Boolean)
    );

    return medicines.filter((medicine) => {
      const medicineKey = String(medicine?.medicine_id || medicine?.medicine_code || medicine?._id || '').trim();
      const isLinkedToSupplier = medicine?.suppliers?.some(s => 
        String(s.supplierId).trim() === selectedId || 
        String(s._id).trim() === selectedId
      );
      const isActive = String(medicine?.status || 'Active').trim() === 'Active';

      return isLinkedToSupplier && (isActive || selectedMedicineIds.has(medicineKey));
    });
  }, [medicines, formData.items, formData.supplier_id]);

  const getMedicineLabel = (med) => {
    if (!med) return '-';
    if (typeof med === 'object') {
      return med.medicine_name?.toUpperCase() || med.medicine_code || med.medicine_id || med._id || '-';
    }
    const found = medicineMap.get(med);
    return found ? (found.medicine_name?.toUpperCase() || found.medicine_code || found.medicine_id || found._id) : med;
  };

  const getPoSeq = (po) => {
    const raw = po?.po_number || po?.po_id || '';
    const match = String(raw).match(/(\d+)/);
    return match?.[1] ? Number(match[1]) : 0;
  };

  const nextPoNumber = useMemo(() => {
    const max = purchaseOrders.reduce((acc, po) => {
      const num = getPoSeq(po);
      return num > acc ? num : acc;
    }, 0);
    return max + 1;
  }, [purchaseOrders]);

  const filteredPurchaseOrders = useMemo(() => {
    const sortedList = [...purchaseOrders].sort((a, b) => {
      const idA = String(a.po_number || a.po_id || a._id || '');
      const idB = String(b.po_number || b.po_id || b._id || '');
      return idB.localeCompare(idA, undefined, { numeric: true, sensitivity: 'base' });
    });

    const q = searchTerm.trim().toLowerCase();
    if (!q) return sortedList;

    return sortedList.filter((po) => {
      const poId = String(po?.po_id || '').toLowerCase();
      const poNumber = String(po?.po_number || '').toLowerCase();
      const supplierLabel = String(getSupplierLabel(po?.supplier_id) || '').toLowerCase();
      const status = String(po?.status || '').toLowerCase();

      return (
        poId.startsWith(q) ||
        poNumber.startsWith(q) ||
        supplierLabel.startsWith(q) ||
        status.startsWith(q)
      );
    });
  }, [purchaseOrders, searchTerm, getSupplierLabel]);

  const paginatedPurchaseOrders = useMemo(() => {
    if (pageSize === -1) return filteredPurchaseOrders;
    const start = (currentPage - 1) * pageSize;
    return filteredPurchaseOrders.slice(start, start + pageSize);
  }, [filteredPurchaseOrders, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filteredPurchaseOrders.length, pageSize]);

  const toDateInput = (value) => toDateInputValue(value);
  const formatDate = (value) => formatDisplayDate(value);

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;

    if (field === 'medicine_id') {
      const med = medicineMap.get(value);
      if (med) {
        const fallbackQty = Number(med.reorder_level || med.minimum_stock_level || 1) || 1;
        const medicineKey = String(med.medicine_id || med.medicine_code || value);
        const latestGrnItem = latestGrnItemByMedicine.get(medicineKey);
        const latestStock = latestStockByMedicine.get(medicineKey);
        newItems[index].ordered_quantity = fallbackQty;
        newItems[index].medicine_name = med.medicine_name || '';
        newItems[index].company_name = med.company_name || '';
        newItems[index].purchase_price = Number(latestGrnItem?.purchase_price ?? latestStock?.purchase_price ?? med.purchase_price ?? 0) || 0;
        newItems[index].mrp = Number(latestGrnItem?.mrp ?? latestStock?.mrp ?? med.mrp ?? med.MRP ?? 0) || 0;
      } else {
        newItems[index].medicine_name = '';
        newItems[index].company_name = '';
        newItems[index].purchase_price = 0;
        newItems[index].mrp = 0;
      }
    }

    if (field === 'medicine_id' || field === 'ordered_quantity' || field === 'purchase_price') {
      const qty = Number(newItems[index].ordered_quantity) || 0;
      const price = Number(newItems[index].purchase_price) || 0;
      newItems[index].total_amount = qty * price;
    }
    setFormData({ ...formData, items: newItems });
  };

  const addItemRow = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { medicine_id: '', medicine_name: '', company_name: '', ordered_quantity: 1, purchase_price: 0, mrp: 0, total_amount: 0 }]
    });
  };

  const removeItemRow = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems.length ? newItems : emptyForm.items });
  };

const resetForm = () => {
    setEditId(null);
    setFormData({
      po_id: '',
      po_number: '',
      supplier_id: '',
      po_date: toDateInputValue(new Date()),
      expected_delivery_date: '',
      created_by: user?.fullName || '',
      remarks: '',
      status: 'Draft',
     
      items: [{ medicine_id: '', medicine_name: '', company_name: '', ordered_quantity: 1, purchase_price: 0, mrp: 0, total_amount: 0 }]
    });
  };

  const handleEdit = (po) => {
    // RESTRICTION: Block editing if rejected or approved
    if (po.status === 'Rejected' || po.status === 'Approved') {
      toast.error(`Cannot edit a ${po.status} order.`);
      return;
    }

    setEditId(po._id);
    setFormData({
      po_id: po.po_id || '',
      po_number: po.po_number || '',
      supplier_id: po.supplier_id?.supplier_id || po.supplier_id || '',
      po_date: toDateInput(po.po_date),
      expected_delivery_date: toDateInput(po.expected_delivery_date),
      created_by: po.created_by || '',
      remarks: po.remarks || '',
      status: po.status || 'Draft',
      items: (po.items || []).map((item) => ({
        medicine_id: item.medicine_id?.medicine_code || item.medicine_id?.medicine_id || item.medicine_id || '',
        medicine_name: item.medicine_name || '',
        company_name: item.company_name || '',
        ordered_quantity: item.ordered_quantity || 1,
        purchase_price: item.purchase_price || 0,
        mrp: item.mrp || 0,
        total_amount: item.total_amount || 0
      }))
    });
    setView('form');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this purchase order?')) return;
    try {
      await api.delete(`/purchase-orders/${id}`);
      if (selectedPO?._id === id) {
        setSelectedPO(null);
        setView('list');
      }
      fetchPOs();
      toast.success('Purchase Order Deleted');
    } catch (err) {
      toast.error('Delete failed');
    }
  };

const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // 1. Determine the PO Number for this transaction
      const currentPoNo = editId ? formData.po_number : `PONo-${nextPoNumber}`;

      const normalizeDateForApi = (value) => {
        const d = toDateObject(value);
        if (!d) return undefined;
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      };

      // 2. Clean items and INJECT the PO Number into every medicine object
      const cleanedItems = (formData.items || [])
        .filter((item) => String(item?.medicine_id || '').trim())
        .map((item) => {
          const qty = Number(item.ordered_quantity) || 0;
          const price = Number(item.purchase_price) || 0;
          const selectedMedicine = medicineMap.get(item.medicine_id);
          
          return {
            medicine_id: item.medicine_id,
            medicine_name: item.medicine_name || selectedMedicine?.medicine_name || '',
            company_name: item.company_name || selectedMedicine?.company_name || '',
            ordered_quantity: qty,
            purchase_price: price,
            mrp: Number(item.mrp || 0) || 0,
            total_amount: qty * price,
            // STORE PO NUMBER INSIDE EVERY MEDICINE
            po_number: currentPoNo 
          };
        });

      if (!cleanedItems.length) {
        toast.error('Add at least one item');
        return;
      }

      // 3. Prepare Payload (po_status is for the whole PO)
      const payload = {
        ...formData,
        po_number: currentPoNo,
        po_date: normalizeDateForApi(formData.po_date),
        expected_delivery_date: normalizeDateForApi(formData.expected_delivery_date),
        items: cleanedItems,
        po_status: formData.po_status || '', // Status for the whole PO
        created_by: formData.created_by || user?.fullName || ''
      };

      // Remove po_id to prevent mongoDB primary key conflicts if your backend auto-generates it
      delete payload.po_id; 

     // 4. Send to API
        if (editId) {
          await api.put(`/purchase-orders/${editId}`, payload);
          toast.success(
            <span>
              Purchase Order <span className="text-blue-600 font-black">{formData.po_number || ''}</span> Updated Successfully
            </span>
          );
        } else {
          const res = await api.post('/purchase-orders', payload);
          const generatedPoNo = res.data?.data?.po_number || '';
          toast.success(
            <span>
              Purchase Order <span className="text-amber-600 font-black">{generatedPoNo}</span> Created Successfully
            </span>
          );
        }

      // 5. Reset UI (Form remains pinned open with flushed states)
      setView('form');
      setEditId(null);
      resetForm();
      fetchPOs();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Error saving PO');
    }
  };

  // Helper for Status Badge Color Coding
  const getStatusStyle = (status) => {
    switch (status) {
      case 'Draft': return 'bg-amber-100 text-amber-700';
      case 'Approved': return 'bg-emerald-100 text-emerald-600';
      case 'Rejected': return 'bg-rose-100 text-rose-600';
      case 'Closed': return 'bg-blue-100 text-blue-700';
      default: return 'bg-slate-200 text-slate-700';
    }
  };
const getFulfillmentStyle = (status) => {
    // If status is empty, null, or undefined
    if (!status || status === "" || status === "---") {
        return 'bg-slate-100 text-slate-400 border border-slate-200';
    }
    
    switch (status) {
      case 'CLOSED': 
        return 'bg-rose-100 text-rose-700 border border-rose-200'; // RED for Closed
      case 'OPEN': 
        return 'bg-indigo-100 text-indigo-700 border border-indigo-200'; // INDIGO for Open
      default: 
        return 'bg-slate-100 text-slate-400 border border-slate-200';
    }
  };

  return (
    <div className={`${masterPageShell} p-2`}>
      <header className={`${masterHeaderCard} mb-1`}>
        <div className="flex items-center gap-1">
          <div className={masterHeaderIcon}><ClipboardList size={10} /></div>
          <div>
            <h1 className={masterTitle}>Purchase Order</h1>
            {/* <p className={masterSubtitle}>Procurement</p> */}
          </div>
        </div>

        <div className="flex items-center gap-2">
       {(view === 'list' || view === 'details') && (
          <button
            onClick={() => { 
              // 1. Completely clear browser state memory history to drop previous values
              window.history.replaceState({}, document.title); 
              location.state = null; 
              
              // 2. Safely wipe out and clean form inputs
              resetForm(); 
              setView('form'); 
            }}
            className={masterAccentButton}
          >
            <Plus size={14} /> Add PO
          </button>
        )}
          {view !== 'list' && (
            <button
              onClick={() => setView('list')}
              className={masterPrimaryButton}
            >
              <ArrowLeft size={14} /> VIEW LIST
            </button>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-7xl">
        {view === 'list' && (
          <div className="max-w-7xl mx-auto bg-white rounded-xl border border-slate-400 overflow-hidden shadow-sm">
              <div>
              <SearchBar 
                value={searchTerm} 
                onChange={(val) => {
                  setSearchTerm(val);
                  setCurrentPage(1);
                }} 
                placeholder="Search alphabetically by PO number, PO ID, or supplier..." 
                totalItems={filteredPurchaseOrders.length} 
                currentPage={currentPage} 
                pageSize={pageSize} 
                onPageChange={setCurrentPage} 
                onPageSizeChange={setPageSize} 
              />
            </div>
            
        <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left">
              <thead>
                  <tr>
                    <th className={masterTableData}>S.No</th>
                    {/* <th className={masterTableData}>PO ID</th> */}
                    <th className={masterTableData}>PO Number</th>
                    <th className={masterTableData}>Supplier</th>
                    <th className={masterTableData}>PO Date</th>
                    <th className={masterTableData}>Expected Delivery</th>
                    <th className={masterTableData}>Status</th>
                    <th className={masterTableData}>PO Status</th>
                    <th className={masterTableData}>Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-300 font-bold text-slate-600">
                  {paginatedPurchaseOrders.length > 0 ? paginatedPurchaseOrders.map((po, index) => (
                    <tr key={po._id} className="hover:bg-emerald-50/30">
                      <td className={masterTableData}> {pageSize === -1 ? index + 1 : (currentPage - 1) * pageSize + index + 1}</td>
                      {/* <td className={masterTableData}> {po.po_id || '-'}</td> */}
                      <td className={masterTableID}> {po.po_number || '-'}</td>
                      <td className={masterTableData} title={getSupplierLabel(po.supplier_id)}>
                        {getSupplierLabel(po.supplier_id)}
                      </td>
                      <td className={masterTableData}> {formatDate(po.po_date)}</td>
                      <td className={masterTableData}> {formatDate(po.expected_delivery_date)}</td>
                      <td className={masterTableData}>
                        <span className={`px-2 py-0.5 rounded text-[9px] uppercase font-bold ${getStatusStyle(po.status)}`}>
                          {po.status || '-'}
                        </span>
                      </td>
               <td className={masterTableData}>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase ${getFulfillmentStyle(po.po_final_status)}`}>
                      {/* If po_final_status exists and is not empty, show it; otherwise show --- */}
                      {po.po_final_status && po.po_final_status.trim() !== "" ? po.po_final_status : "---"}
                    </span>
                  </td>
                      <td className={masterTableData}>
                        <button
                          onClick={() => { setSelectedPO(po); setView('details'); }}
                          className="p-1 text-emerald-600 rounded-lg hover:bg-emerald-100"
                          title="View Items"
                        >
                          <Eye size={14} />
                        </button>
                        <button
                          onClick={() => handleEdit(po)}
                          className={`ml-2 p-1 rounded-lg ${po.status === 'Draft' ? 'text-sky-600 hover:bg-sky-100' : 'text-slate-300 cursor-not-allowed'}`}
                          title={po.status === 'Draft' ? "Edit" : "Cannot edit processed order"}
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(po._id)}
                          className="ml-2 p-1 text-rose-600 rounded-lg hover:bg-rose-100"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="8" className="p-12 text-center text-slate-300 font-medium uppercase text-xs">No matching records found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {view === 'form' && (
          <form onSubmit={handleSubmit} className={`${masterPanelPadding} space-y-1`}>
            <div className="flex items-center justify-between border-b">
              <h4 className="text-xs font-bold text-slate-700 uppercase">{editId ? 'Edit Purchase Order' : 'New Purchase Order'}</h4>
              <button
                type="button"
                onClick={resetForm}
                className="text-[10px] font-bold text-rose-500 flex items-center gap-1 uppercase hover:underline"
              >
                Reset
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-1 gap-y-1">
              {/* <ReadOnly label="PO ID" value={editId ? (formData.po_id || '-') : `PO-${nextPoNumber}`} /> */}
              {/* <ReadOnly label="PO No" value={editId ? (formData.po_number || '-') : `PONo-${nextPoNumber}`} /> */}
              <Select
                label="Supplier"
                value={formData.supplier_id}
                onChange={(e) => setFormData({
                  ...formData,
                  supplier_id: e.target.value,
                  items: emptyForm.items
                })}
                options={suppliers.map((s) => ({ value: s.supplier_id || s._id, label: `${s.supplier_name}` }))}
                placeholder="Select Supplier"
                required
              />
              <Input
                label="PO Date"
                type="text"
                inputMode="numeric"
                maxLength={10}
                placeholder="DD/MM/YYYY"
                value={formData.po_date}
                onChange={(e) => setFormData({ ...formData, po_date: normalizeDateInput(e.target.value) })}
                required
              />
              <Input
                label="Expected Delivery"
                type="text"
                inputMode="numeric"
                maxLength={10}
                placeholder="DD/MM/YYYY"
                value={formData.expected_delivery_date}
                onChange={(e) => setFormData({ ...formData, expected_delivery_date: normalizeDateInput(e.target.value) })}
              />
             <ReadOnly label="Created By" value={formData.created_by || user?.fullName || ''} />
              <Input label="Remarks" value={formData.remarks} onChange={(e) => setFormData({ ...formData, remarks: e.target.value })} />
              <Select
                label="Status"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                // REMOVED 'Approved' from options as per requirement
                options={['Draft'].map((s) => ({ value: s, label: s }))}
              />
            </div>

         <div className="border-t pt-0.5 w-full">
              <div className="flex items-center justify-between mb-0.5 px-1">
                <h3 className="text-[11px] font-black uppercase text-emerald-600 tracking-widest">PO Items</h3>
                <button 
                  type="button" 
                  onClick={addItemRow} 
                  className="text-[10px] font-bold uppercase text-indigo-600 hover:underline flex items-center gap-1"
                >
                  <Plus size={12} /> Add Another Item
                </button>
              </div>

              <div className="overflow-x-auto rounded-xl border border-slate-300">
                <table className="w-full min-w-[880px] text-left text-[11px]">
                  <thead className="bg-slate-100 text-[9px] uppercase font-black text-emerald-600 tracking-widest border-b border-slate-300">
                    <tr>
                      <th className="p-0.5">S.No</th>
                      <th className="p-0.5">Medicine Name</th>
                      <th className="p-0.5">Company</th>
                      <th className="p-0.5 text-right">Qty</th>
                      <th className="p-0.5 text-right">Price</th>
                      <th className="p-0.5 text-right">Total</th>
                      <th className="p-0.5 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 bg-white">
                    {formData.items.map((item, index) => (
                      <tr key={index}>
                        <td className="p-0.5 align-middle text-center font-semibold text-slate-700">{index + 1}</td>
                        <td className="p-0.5 align-middle">
                          <select
                            value={item.medicine_id}
                            onChange={(e) => handleItemChange(index, 'medicine_id', e.target.value)}
                            className={masterSelect}
                            disabled={!formData.supplier_id}
                            required
                          >
                            <option value="">{formData.supplier_id ? 'SELECT MEDICINE' : 'SELECT SUPPLIER FIRST'}</option>
                            {supplierMedicines.map((m) => (
                              <option key={m.medicine_code || m.medicine_id || m._id} value={m.medicine_code || m.medicine_id || m._id}>
                                {m.medicine_name || m.medicine_code || m.medicine_id || m._id}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="p-0.5 align-middle">
                          <input value={item.company_name || ''} className={masterReadOnly} disabled readOnly />
                        </td>
                        <td className="p-0.5 align-middle">
                          <input
                            type="number"
                            value={item.ordered_quantity === 0 ? '' : item.ordered_quantity}
                            onChange={(e) => handleItemChange(index, 'ordered_quantity', e.target.value)}
                            placeholder="0"
                            className={`${masterInput} text-right`}
                          />
                        </td>
                        <td className="p-0.5 align-middle">
                          <input
                            type="number"
                            value={item.purchase_price === 0 ? '' : item.purchase_price}
                            onChange={(e) => handleItemChange(index, 'purchase_price', e.target.value)}
                            placeholder="0.00"
                            className={`${masterInput} text-right`}
                          />
                        </td>
                        <td className="p-0.5 align-middle">
                          <div className={`${masterReadOnly} text-right`}>{item.total_amount || '0.00'}</div>
                        </td>
                        <td className="p-0.5 align-middle text-center">
                          <button
                            type="button"
                            onClick={() => removeItemRow(index)}
                            className="inline-flex p-2 rounded-lg text-rose-600 hover:bg-rose-100 transition-all"
                            title="Remove"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="pt-1 border-t flex justify-end">
              <button type="submit" className={`${masterAccentButton} px-4 py-1`}>
                {editId ? 'Update' : 'Save'} Purchase Order
              </button>
            </div>
          </form>
        )}

        {view === 'details' && selectedPO && (
          <div className={masterPanelPadding}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-lg font-bold text-slate-900">{selectedPO.po_id || 'PO'}</h2>
                <p className="text-[11px] text-slate-500">PO Number: {selectedPO.po_number || '-'}</p>
              </div>
              <div className="text-right text-[11px] flex items-start gap-2">
                <button
                  onClick={() => setView('list')}
                  className="p-0.5 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-900 hover:text-white"
                  title="Close"
                >
                  <X size={14} />
                </button>
                <div>
                  <p className="font-bold">Supplier: {getSupplierLabel(selectedPO.supplier_id)}</p>
                  <p>Status: 
                    <span className={`ml-1 px-2 py-0.5 rounded ${getStatusStyle(selectedPO.status)}`}>
                      {selectedPO.status || '-'}
                    </span>
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px] text-slate-700 mb-4">
              <div><span className="font-bold">PO Date:</span> {formatDate(selectedPO.po_date)}</div>
              <div><span className="font-bold">Expected Delivery:</span> {formatDate(selectedPO.expected_delivery_date)}</div>
              <div><span className="font-bold">Created By:</span> {selectedPO.created_by || '-'}</div>
              <div><span className="font-bold">Remarks:</span> {selectedPO.remarks || '-'}</div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-slate-300">
              <table className="w-full text-left text-[11px]">
                <thead className="bg-slate-100 text-[9px] uppercase font-black text-emerald-600 tracking-widest border-b border-slate-400">
                  <tr>
                    <th className="p-3">Medicine</th>
                    <th className="p-3">Company</th>
                    <th className="p-3">Quantity</th>
                    <th className="p-3">Price</th>
                    {/* <th className="p-3">MRP</th> */}
                    <th className="p-3 text-right">Subtotal</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-300 font-bold text-slate-600">
                  {(selectedPO.items || []).map((item, idx) => (
                    <tr key={idx}>
                      <td className="p-3">{getMedicineLabel(item.medicine_id)}</td>
                      <td className="p-3">{item.company_name || medicineMap.get(item.medicine_id)?.company_name || '-'}</td>
                      <td className="p-3">{item.ordered_quantity}</td>
                      <td className="p-3">{item.purchase_price}</td>
                      {/* <td className="p-3">{item.mrp || '-'}</td> */}
                      <td className="p-3 text-right">{item.total_amount}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50 font-bold text-slate-800">
                  <tr>
                    <td colSpan="5" className="p-3 text-right">Grand Total:</td>
                    <td className="p-3 text-right">
                      {(selectedPO.items || []).reduce((sum, item) => sum + (Number(item.total_amount) || 0), 0)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

const Input = ({ label, required, ...props }) => (
  <div className="flex flex-col">
    <label className={masterInputLabel}>{label}</label>
    <input required={required} {...props} className={masterInput} />
  </div>
);

const ReadOnly = ({ label, value }) => (
  <div className="flex flex-col">
    <label className={masterInputLabel}>{label}</label>
    <div className={masterReadOnly}>{value}</div>
  </div>
);

const Select = ({ label, options, placeholder, ...props }) => (
  <div className="flex flex-col">
    <label className={masterInputLabel}>{label}</label>
    <select {...props} className={masterSelect}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(opt => (
        <option key={opt.value || opt} value={opt.value || opt}>{opt.label || opt}</option>
      ))}
    </select>
  </div>
);

export default PurchaseOrder;
